/**
 * PsychometricTest
 *
 * Container component managing the full psychometric test flow.
 * Renders MBTI questions with auto-advance, timer, and progress bar.
 * No video/audio/screen recording (recording:false in snapshot).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { DEFAULT_EXAM_LANGUAGE, type ExamLanguage } from '../../types/exam-config.types';
import { PsychometricSectionSnapshot } from '../../types/interview.types';
import { usePsychometricInterview } from '../../hooks/usePsychometricInterview';
import { MBTIQuestionCard } from './MBTIQuestionCard';
import { Button } from '@/components/ui/atoms/Button';
import { ProgressBar } from '../ui/atoms/ProgressBar';
import { resolvePsychometricTestName, type JobTFunction } from '../../utils/psychometric-test-copy';
import { getFixedTForExamLanguage, examLanguageToLocale } from '@/utils/locale';
import { ChevronLeft } from 'lucide-react';

interface PsychometricTestProps {
  interviewId: string;
  psychometricSections: PsychometricSectionSnapshot[];
  onComplete: () => void;
  onEndInterview: () => void;
  interviewLanguage?: ExamLanguage;
}

function useTestTimer(estimatedMinutes: number, resetKey: number) {
  const [secondsLeft, setSecondsLeft] = useState(estimatedMinutes * 60);
  const [isOvertime, setIsOvertime] = useState(false);
  const [overtimeSeconds, setOvertimeSeconds] = useState(0);

  useEffect(() => {
    setSecondsLeft(estimatedMinutes * 60);
    setIsOvertime(false);
    setOvertimeSeconds(0);
  }, [resetKey, estimatedMinutes]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 0) {
          setIsOvertime(true);
          setOvertimeSeconds((o) => o + 1);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resetKey]);

  const format = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return { secondsLeft, isOvertime, overtimeSeconds, format };
}

export function PsychometricTest({
  interviewId,
  psychometricSections,
  onComplete,
  onEndInterview,
  interviewLanguage = DEFAULT_EXAM_LANGUAGE,
}: PsychometricTestProps) {
  const { t } = useTranslation('interview');
  const tExam = useMemo<JobTFunction>(
    () => getFixedTForExamLanguage(interviewLanguage),
    [interviewLanguage],
  );
  const contentLocale = useMemo(
    () => examLanguageToLocale(interviewLanguage),
    [interviewLanguage],
  );
  const {
    currentTestIndex,
    currentQuestionIndex,
    currentSection,
    currentQuestion,
    canGoPrevious,
    isLastQuestion,
    progress,
    setAnswer,
    currentAnswer,
    goToNext,
    goToPrevious,
    completeTest,
    unansweredCount,
  } = usePsychometricInterview(interviewId, psychometricSections);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  const currentEstimatedMinutes = currentSection?.estimated_minutes ?? 15;

  // Restart timer when test section changes
  const [timerKey, setTimerKey] = useState(0);
  useEffect(() => {
    setTimerKey((k) => k + 1);
  }, [currentTestIndex]);

  const { secondsLeft, isOvertime, overtimeSeconds, format } = useTestTimer(currentEstimatedMinutes, timerKey);

  // Calculate global question number
  const globalQuestionNumber =
    psychometricSections.slice(0, currentTestIndex).reduce(
      (sum, s) => sum + s.questions_data.length,
      0
    ) + currentQuestionIndex + 1;

  const totalQuestionsAll = psychometricSections.reduce(
    (sum, s) => sum + s.questions_data.length,
    0
  );

  // Auto-advance: when an answer is selected, advance after 400ms
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAnswer = useCallback((value: number) => {
    setAnswer(value);

    // Clear any pending auto-advance
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
    }

    // Auto-advance unless it's the last question
    if (!isLastQuestion) {
      autoAdvanceTimer.current = setTimeout(() => {
        goToNext();
      }, 400);
    }
  }, [setAnswer, isLastQuestion, goToNext]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  const handleSubmit = useCallback(async () => {
    if (unansweredCount > 0) {
      setShowCompleteConfirm(true);
      return;
    }
    await doComplete();
  }, [unansweredCount]);

  const doComplete = useCallback(async () => {
    setIsSubmitting(true);
    setShowCompleteConfirm(false);
    try {
      await completeTest();
      onComplete();
    } catch (err) {
      console.error('[PsychometricTest] Error completing test:', err);
      toast.error(i18n.t('interview:toasts.psychometric_submit_failed'));
      setIsSubmitting(false);
    }
  }, [completeTest, onComplete]);

  // When timer reaches 0, auto-submit answers and end interview
  const hasAutoSubmitted = useRef(false);
  useEffect(() => {
    if (isOvertime && !hasAutoSubmitted.current && !isSubmitting) {
      hasAutoSubmitted.current = true;
      console.log('[PsychometricTest] Timer expired. Auto-submitting and ending interview.');
      doComplete();
    }
  }, [isOvertime, isSubmitting, doComplete]);

  if (!currentSection || !currentQuestion) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500 font-body">{t('live.psychometric.no_questions')}</p>
      </div>
    );
  }

  const currentTestName = resolvePsychometricTestName(
    currentSection.test_slug,
    currentSection.test_name,
    tExam,
  );

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Top bar */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-gradient-to-r from-purple/5 to-light-purple/10 px-6 py-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-base font-bold text-dark-invirtus font-heading">
              {currentTestName}
            </h2>
            {psychometricSections.length > 1 && (
              <p className="text-xs text-gray-500 font-body">
                {t('live.psychometric.test_of', {
                  current: currentTestIndex + 1,
                  total: psychometricSections.length,
                })}
              </p>
            )}
          </div>

          {/* Timer */}
          <div
            className={[
              'flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold font-heading',
              isOvertime
                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                : secondsLeft < 120
                ? 'bg-red-50 text-red-600 border border-red-200'
                : 'bg-white text-dark-invirtus border border-gray-200',
            ].join(' ')}
          >
            <span>{isOvertime ? '+' : ''}</span>
            <span>{isOvertime ? format(overtimeSeconds) : format(secondsLeft)}</span>
            {isOvertime && (
              <span className="text-xs font-normal">{t('live.psychometric.over')}</span>
            )}
          </div>
        </div>

        {/* Progress bar + back button */}
        <div className="flex items-center gap-3">
          {/* Previous question button */}
          <Button
            type="icon"
            intent="muted"
            size="xs"
            icon={<ChevronLeft />}
            onClick={goToPrevious}
            disabled={!canGoPrevious || isSubmitting}
            aria-label={t('live.psychometric.previous_aria')}
            className="flex-shrink-0 disabled:opacity-0"
          />
          <ProgressBar
            value={progress}
            max={100}
            size="md"
            intent="primary"
            className="flex-1"
            aria-label={`${globalQuestionNumber}/${totalQuestionsAll}`}
          />
          <span className="text-xs text-gray-500 font-body whitespace-nowrap">
            {globalQuestionNumber}/{totalQuestionsAll}
          </span>
        </div>
      </div>

      {/* Multi-test stepper (only if multiple sections) */}
      {psychometricSections.length > 1 && (
        <div className="flex-shrink-0 flex items-center justify-center gap-3 px-6 py-2 border-b border-gray-100">
          {psychometricSections.map((section, idx) => (
            <div key={section.test_id} className="flex items-center gap-1">
              <div
                className={[
                  'w-2 h-2 rounded-full',
                  idx < currentTestIndex
                    ? 'bg-green-400'
                    : idx === currentTestIndex
                    ? 'bg-purple'
                    : 'bg-gray-200',
                ].join(' ')}
              />
              <span className="text-xs text-gray-400 font-body">
                {resolvePsychometricTestName(section.test_slug, section.test_name, tExam)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Question area — scrollable */}
      <div className="flex-1 overflow-y-auto flex items-center justify-center py-8 px-4">
        <div className="w-full max-w-2xl">
          {/* Keyed wrapper for fade transition on question change */}
          <div
            key={currentQuestion.id}
            className="animate-fade-in"
          >
            <MBTIQuestionCard
              question={currentQuestion}
              answer={currentAnswer}
              onAnswer={handleAnswer}
              contentLocale={contentLocale}
            />
          </div>

        </div>
      </div>

      {/* Bottom action bar */}
      <div className="flex-shrink-0 p-4 pb-2 border-t border-gray-200 bg-gray-50">
        {isLastQuestion ? (
          <div className="flex flex-col items-center gap-1 w-full">
            {unansweredCount > 0 && (
              <span className="text-xs text-amber-600 font-body">
                {t('live.psychometric.unanswered', { count: unansweredCount })}
              </span>
            )}
            <div className="w-full [&>div]:w-full">
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                label={isSubmitting ? t('live.psychometric.submitting') : t('live.psychometric.end_submit_assessment')}
                type="contained"
                intent="primary"
                className="w-full"
              />
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <Button
              onClick={onEndInterview}
              label={t('live.actions.end_interview')}
              type="text"
              intent="danger"
              disabled={isSubmitting}
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Confirmation modal for unanswered questions */}
      {showCompleteConfirm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-bold text-dark-invirtus font-heading mb-2">
              {t('live.psychometric.confirm_title')}
            </h3>
            <p className="text-dark-invirtus font-body mb-5">
              {t('live.psychometric.confirm_message', { count: unansweredCount })}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => setShowCompleteConfirm(false)}
                label={t('live.psychometric.go_back')}
                type="outlined"
                intent="secondary"
              />
              <Button
                onClick={doComplete}
                label={t('live.psychometric.submit_anyway')}
                type="contained"
                intent="secondary"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
